'use client';

import { InjectableMenuSpacer } from '@/components/InjectableMenuSpacer';
// import { UserAvatarMenu } from "@/components/widget/user-avatar-menu";
import { MegaMenu } from '@/components/MegaMenu';
import SignupModal from '@/components/SignupModal';

import { useIsAdmin } from '@/components/auth/permission-provider';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Link, usePathname, useRouter } from '@/i18n/navigation';
import { getErrorMessage } from '@/lib/error-utils';
import { getCreditBalance } from '@/server/actions/credit-actions';
import { useAuthLoading, useIsAuthenticated } from '@/store/auth-store';
import { useAuthInitialized, useSignOut, useUser } from '@/store/auth-store';
import { ChevronDown, Menu, Popcorn, X, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { AuthButtons } from './header/AuthButtons';
import { MobileMenu } from './header/MobileMenu';

export default function Header({ isInjectable = false }: { isInjectable?: boolean }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const t = useTranslations('userMenu');
  const user = useUser();
  const signOut = useSignOut();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAdmin = useIsAdmin();
  const isAuthInitialized = useAuthInitialized();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const linkTarget = isInjectable ? '_parent' : undefined;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const [balance, setBalance] = useState<number | null>(null);
  const [periodTotal, setPeriodTotal] = useState<number | null>(null);

  useEffect(() => {
    const fetchBalance = async () => {
      if (isAuthenticated) {
        try {
          const result = await getCreditBalance();
          if (result.success && result.data) {
            setBalance(result.data.availableBalance);
            setPeriodTotal(result.data.periodTotal);
          }
        } catch (error) {
          console.error('Failed to fetch balance:', getErrorMessage(error));
        }
      }
    };

    // Delay fetch slightly to avoid concurrent redundant requests on page boot
    const timer = setTimeout(fetchBalance, 1000);

    // Listen for global credit update events
    const handleCreditUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent?.detail?.optimisticBalance !== undefined) {
        setBalance(customEvent.detail.optimisticBalance);
      } else {
        fetchBalance();
      }
    };

    window.addEventListener('credit-update', handleCreditUpdate as EventListener);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('credit-update', handleCreditUpdate as EventListener);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam === 'login' || authParam === 'signup' || authParam === 'reset-password') {
      setIsAuthModalOpen(true);
    }
  }, [searchParams]);

  const handleLogout = async () => {
    // Check if current path is a protected route
    // Use includes to handle locale prefixes (e.g. /en/settings)
    const isProtectedRoute = pathname.includes('/user/') || pathname.includes('/admin/');

    if (isProtectedRoute) {
      setIsSigningOut(true);
      closeMenu(); // Close immediately for better UX
      router.push('/');

      // Wait for navigation to complete before signing out to avoid flash of access denied
      const checkInterval = setInterval(async () => {
        const currentPath = window.location.pathname;
        const isStillProtected = currentPath.includes('/user/') || currentPath.includes('/admin/');

        if (!isStillProtected) {
          clearInterval(checkInterval);
          // Small delay to ensure unmount
          setTimeout(async () => {
            await signOut();
            closeMenu();
          }, 100);
        }
      }, 50);

      // Safety timeout
      setTimeout(() => {
        clearInterval(checkInterval);
        signOut().then(closeMenu);
      }, 2000);
      return;
    }

    setIsSigningOut(true);
    closeMenu();
    await signOut();
  };

  const handleHoverEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsUserMenuOpen(true);
  };

  const handleHoverLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 200); // 200ms delay to bridge the gap
  };

  return (
    <nav className="navbar force-light !z-[999] h-[72px] bg-white">
      {isInjectable && <InjectableMenuSpacer />}
      <div className="nav-container h-full">
        <div className="flex h-full items-center gap-2 sm:gap-4 lg:gap-8">
          <div className="logo flex h-full items-center">
            <Link href="/" aria-label="ZappingAI Home" target={linkTarget}>
              <Image
                src="/images/zappingai-logo-small.png"
                alt="ZappingAI"
                width={300}
                height={109}
                className="logo-img w-auto"
                priority
                sizes="(max-width: 640px) 111px, (max-width: 1024px) 130px, 150px"
              />
            </Link>
          </div>

          {/* Desktop Mega Menu */}
          <div className="hidden h-full items-center gap-2 md:flex lg:gap-4">
            <MegaMenu isInjectable={isInjectable} />
          </div>
        </div>

        <div className="ml-auto flex h-full items-center gap-2 sm:gap-4">
          {!isMenuOpen && isAuthenticated && (
            <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap sm:gap-4">
              <Link
                href="/user/credits"
                target={linkTarget}
                className="group !text-black hover:!text-primary hidden h-12 cursor-pointer items-center justify-center gap-2 rounded-md bg-transparent px-2 font-bold text-[1.2rem] transition-colors hover:bg-zinc-200 focus:bg-zinc-200 focus:text-primary md:px-3 lg:px-4 min-[380px]:flex"
              >
                <div className="flex items-center gap-1.5">
                  {balance === null && periodTotal === null ? (
                    <div className="shimmer-base flex h-6 items-center gap-1 rounded-md px-1.5 opacity-60">
                      <Zap className="h-3.5 w-3.5 fill-current text-black/20" />
                      <div className="h-1.5 w-5 rounded-full bg-black/10" />
                    </div>
                  ) : (
                    <>
                      <Zap className="!text-black group-hover:!text-primary h-4 w-4 fill-current" />
                      <span className="!text-black group-hover:!text-primary transition-colors">
                        {balance === 0 ? '350+' : balance?.toLocaleString()}
                      </span>
                    </>
                  )}
                  <span className="!text-black group-hover:!text-primary ml-0.5 transition-colors">
                    {t('creditBalance')}
                  </span>
                </div>
              </Link>

              <Link
                href="/user/gallery"
                target={linkTarget}
                className="group !text-black hover:!text-primary hidden h-12 cursor-pointer items-center justify-center gap-2 rounded-md bg-transparent px-2 font-bold text-[1.2rem] transition-colors hover:bg-zinc-200 focus:bg-zinc-200 focus:text-primary md:px-3 lg:px-4 min-[870px]:flex"
              >
                <Popcorn className="h-5 w-5 transition-colors" />
                <span className="">Gallery</span>
              </Link>

              <div
                className="hidden h-full items-center md:flex"
                onMouseEnter={handleHoverEnter}
                onMouseLeave={handleHoverLeave}
              >
                <DropdownMenu open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      aria-label={
                        user?.name || user?.lastName
                          ? `Account menu for ${`${user.name || ''} ${user.lastName || ''}`.trim()}`
                          : 'Account menu'
                      }
                      className="flex h-12 cursor-pointer select-none items-center gap-2 rounded-full border-none bg-transparent p-0 outline-none hover:bg-zinc-200 focus:bg-zinc-200 data-[state=open]:bg-zinc-100"
                    >
                      <Avatar className="h-[32px] w-[32px] cursor-pointer transition-transform hover:scale-105">
                        <AvatarImage
                          src={user?.image || undefined}
                          alt={
                            user?.name || user?.lastName
                              ? `${user?.name || ''} ${user?.lastName || ''}`.trim()
                              : user?.email || 'User'
                          }
                        />
                        <AvatarFallback className="font-normal text-sm">
                          {user?.name && user?.lastName
                            ? `${user.name[0]}${user.lastName[0]}`.toUpperCase()
                            : (user?.name?.[0]?.toUpperCase() ??
                              user?.email?.[0]?.toUpperCase() ??
                              'U')}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="size-4 text-black/50" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-auto border border-gray-200 bg-white p-0 shadow-lg"
                    align="end"
                    sideOffset={18}
                    onMouseEnter={handleHoverEnter}
                    onMouseLeave={handleHoverLeave}
                  >
                    <div
                      className={`grid gap-4 p-4 ${isAdmin ? 'md:w-[550px] lg:w-[650px] lg:grid-cols-3' : 'md:w-[380px] lg:w-[450px] lg:grid-cols-2'}`}
                    >
                      {/* Admin Column (leftmost, only shown for admins) */}
                      {isAdmin && (
                        <div className="flex flex-col gap-2">
                          <div className="px-2 py-1.5 font-bold text-gray-500 text-xs uppercase tracking-wider">
                            Admin
                          </div>
                          <Link
                            href="/admin/services"
                            target={linkTarget}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                          >
                            <div className="font-bold text-base leading-none">Services</div>
                            <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                              Manage system services
                            </p>
                          </Link>
                          <Link
                            href="/admin/users"
                            target={linkTarget}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                          >
                            <div className="font-bold text-base leading-none">Users</div>
                            <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                              Manage registered users
                            </p>
                          </Link>
                          <Link
                            href="/admin/marketing"
                            target={linkTarget}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                          >
                            <div className="font-bold text-base leading-none">Marketing</div>
                            <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                              View marketing stats
                            </p>
                          </Link>
                        </div>
                      )}

                      {/* Help & Support Column */}
                      <div className="flex flex-col gap-2">
                        <div className="px-2 py-1.5 font-bold text-gray-500 text-xs uppercase tracking-wider">
                          Help & Support
                        </div>
                        <Link
                          href="/pricing"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">
                            See Plans & Pricing
                          </div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            View pricing plans
                          </p>
                        </Link>
                        <Link
                          href="/how-it-works"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">How it works</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Learn the process
                          </p>
                        </Link>
                        <Link
                          href="/help"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">FAQs & Help</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Get answers
                          </p>
                        </Link>
                        <Link
                          href="/contact"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">Contact Us</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Get in touch
                          </p>
                        </Link>
                      </div>

                      {/* Account Column (rightmost) */}
                      <div className="flex flex-col gap-2">
                        <div className="px-2 py-1.5 font-bold text-gray-500 text-xs uppercase tracking-wider">
                          Account
                        </div>
                        <Link
                          href="/user/profile"
                          target={linkTarget}
                          className="group block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-[40px] w-[40px]">
                              <AvatarImage
                                src={user?.image || undefined}
                                alt={
                                  user?.name || user?.lastName
                                    ? `${user?.name || ''} ${user?.lastName || ''}`.trim()
                                    : user?.email || 'User'
                                }
                              />
                              <AvatarFallback className="font-normal text-sm">
                                {user?.name && user?.lastName
                                  ? `${user.name[0]}${user.lastName[0]}`.toUpperCase()
                                  : (user?.name?.[0]?.toUpperCase() ??
                                    user?.email?.[0]?.toUpperCase() ??
                                    'U')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="max-w-[200px] truncate font-bold text-base text-foreground transition-colors group-hover:text-primary leading-none">
                                {user?.name || user?.lastName
                                  ? `${user.name || ''} ${user.lastName || ''}`.trim()
                                  : t('user')}
                              </span>
                              <span className="mt-1 text-muted-foreground text-sm">
                                {user?.email}
                              </span>
                            </div>
                          </div>
                        </Link>

                        <div className="mt-1" />

                        <Link
                          href="/user/billing"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">{t('billing')}</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            View history and plans
                          </p>
                        </Link>

                        <Link
                          href="/user/security"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">{t('security')}</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Update password and settings
                          </p>
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="block w-full cursor-pointer select-none space-y-1 rounded-md p-3 text-left leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary"
                        >
                          <div className="font-bold text-base leading-none">{t('logout')}</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Sign out of your account
                          </p>
                        </button>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
          {!isAuthenticated && (
            <div className="hidden md:flex">
              <AuthButtons
                isAuthenticated={isAuthenticated}
                setIsAuthModalOpen={setIsAuthModalOpen}
                closeMenu={closeMenu}
                linkTarget={linkTarget}
              />
            </div>
          )}
          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-foreground md:hidden"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            type="button"
          >
            {isMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
          </button>
        </div>

        <MobileMenu
          isMenuOpen={isMenuOpen}
          closeMenu={closeMenu}
          isAuthenticated={isAuthenticated}
          isSigningOut={isSigningOut}
          user={user}
          handleLogout={handleLogout}
          isAdmin={isAdmin}
          setIsAuthModalOpen={setIsAuthModalOpen}
          linkTarget={linkTarget}
          balance={balance}
          periodTotal={periodTotal}
        />
      </div>
      <SignupModal open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
    </nav>
  );
}
